import {trackEvent} from "@atg-shared/analytics";
import {fromMinor} from "@atg-shared/currency";
import {now, parseDateTimestamp} from "@atg-shared/datetime";
import {noTrans} from "@atg-shared/no-trans";
import {GameType} from "@atg-tillsammans/types/generated";
import {formatGameTypeToSport} from "@atg-tillsammans-shared/utils";

import {
    MENU_CLICK,
    TEAM_CARD_CLICK,
    TEAM_CHAT,
    START_PAGE_CLICK,
    type LOGIN_STARTED,
    type LOGIN_COMPLETE,
    type TillsammansTrackingEvent,
    TEAM_PAGE_CLICK,
    TEAM_SEARCH,
    TERMINATE_TEAM_CLICK,
    PURCHASE_SHARE,
    PLACE_BET,
    SHARED_BET_SEARCH,
    PRIVATE_SHARE_SEARCH,
    PRODUCT_PAGE_V85,
    CAPTAIN_EXCLUSIVE_TIP,
    MY_TEAMS_PAGE_CLICK,
    APPLY_TO_JOIN_TEAM,
    PRE_BOOKING_CLICK,
    PRE_BOOKING_EDIT,
    TOGGLE_STAKE_CHOICE,
    MY_COUPON_CLICK,
} from "./tillsammansAnalytics.types";
import {toAnalyticsFormat} from "./tillsammansAnalyticsUtils";

import type {TrackEvent} from "@atg-shared/analytics";

const trackTillsammansEvent: TrackEvent<TillsammansTrackingEvent> = trackEvent;

export const trackClickArea = (clickArea?: string | number) => {
    trackTillsammansEvent({
        event: TEAM_CARD_CLICK,
        clickedSection: `Area Clicked: ${clickArea}`,
    });
};

export const trackTeamChatSend = (teamName: string) => {
    trackTillsammansEvent({
        event: TEAM_CHAT,
        clickedAction: "sent message in chat",
        teamName,
    });
};

type LoginEvent = typeof LOGIN_STARTED | typeof LOGIN_COMPLETE;

export const trackLoginEvent = (event: LoginEvent) => {
    trackTillsammansEvent({
        event,
    });
};

export const trackSideMenuClick = (label: string) => {
    trackTillsammansEvent({
        event: MENU_CLICK,
        menuType: "tillsammans",
        menuLabel: toAnalyticsFormat(label),
    });
};

export const trackStartPageClick = ({
    label,
    location,
    target,
    area,
}: {
    label: string;
    location: string;
    target?: string | undefined;
    area?: string | undefined;
}) => {
    trackTillsammansEvent({
        event: START_PAGE_CLICK,
        startPageObject: label.toLowerCase(),
        resultOfClick: location,
        target,
        area,
    });
};

export const trackStartPageTeamCardClick = ({
    label,
    location,
    teamId,
}: {
    label: string;
    location: string;
    teamId: string;
}) => {
    trackStartPageClick({
        label,
        location,
        target: `team-${teamId}`,
    });
};

export const trackStartPageShareCardClick = ({
    label,
    location,
    shareId,
}: {
    label: string;
    location: string;
    shareId: string;
}) => {
    trackStartPageClick({
        label,
        location,
        target: `share-${shareId}`,
    });
};

export const trackTeamPageClick = ({
    label,
    location,
    area,
}: {
    label: string;
    location?: string | undefined;
    area?: string | undefined;
}) => {
    trackTillsammansEvent({
        event: TEAM_PAGE_CLICK,
        teamPageObject: label,
        resultOfClick: location,
        area,
    });
};

export const trackTeamSearchEvent = ({
    eventAction,
    eventType,
    value,
}: {
    eventAction: string;
    eventType?: string | string[] | undefined;
    value?: string | undefined;
}) => {
    trackTillsammansEvent({
        event: TEAM_SEARCH,
        eventAction,
        eventType,
        value,
    });
};
export const trackApplyToJoinTeamClick = ({
    label,
    area,
}: {
    label: string;
    area: string;
}) => {
    trackTillsammansEvent({
        event: APPLY_TO_JOIN_TEAM,
        startPageObject: label,
        area,
    });
};

export const trackPreBookingClick = ({
    gameType,
    amount,
    choice,
    type,
}: {
    gameType?: GameType | undefined;
    amount?: string | undefined;
    choice?: string | undefined;
    type?: string | undefined;
}) => {
    trackTillsammansEvent({
        event: PRE_BOOKING_CLICK,
        gameType,
        amount,
        choice,
        type,
    });
};
export const trackPreBookingEdit = ({choice}: {choice: string}) => {
    trackTillsammansEvent({
        event: PRE_BOOKING_EDIT,
        choice,
    });
};

export const trackSharedBetSearchEvent = ({
    eventAction,
    eventType,
    value,
}: {
    eventAction: string;
    eventType?: string | string[] | undefined;
    value?: string | undefined;
}) => {
    trackTillsammansEvent({
        event: SHARED_BET_SEARCH,
        eventAction,
        eventType,
        value,
    });
};

export const trackPrivateShareSearchEvent = ({
    eventAction,
    eventType,
    searchText,
}: {
    eventAction: string;
    eventType?: string | string[] | undefined;
    searchText: string | undefined;
}) => {
    trackTillsammansEvent({
        event: PRIVATE_SHARE_SEARCH,
        eventAction,
        eventType,
        searchText,
    });
};

export const trackTerminateTeamEvent = ({teamName}: {teamName: string}) => {
    trackTillsammansEvent({
        event: TERMINATE_TEAM_CLICK,
        teamName,
    });
};

export const trackPurchaseShareEvent = ({
    cost,
    name,
    variant,
    category,
    id,
}: {
    cost: number;
    name: GameType;
    variant: string;
    category: string;
    id: string;
}) => {
    trackTillsammansEvent({
        event: PURCHASE_SHARE,
        area: undefined,
        cost: cost ? cost / 100 : undefined,
        name,
        variant,
        category,
        id,
    });
};

type PlaceBetProps = {
    id: string;
    gameType: GameType;
    gameDate: string;
    stake: number;
    numberOfCoupons: number;
    tracks?: string[];
    variant: string;
};

export const trackPlaceBetEvent = ({
    gameType,
    gameDate,
    id,
    stake,
    numberOfCoupons,
    tracks,
    variant,
}: PlaceBetProps) => {
    const today = now();
    const product = {
        category: formatGameTypeToSport(gameType),
        id:
            gameType === GameType.BIG9
                ? id
                : `${gameType}_${tracks}_${parseDateTimestamp(gameDate).format(
                      "YYYY-MM-DD",
                  )}`,
        name: gameType,
        price: `${(fromMinor(stake) || 0) / numberOfCoupons}`,
        quantity: `${numberOfCoupons}`,
        track:
            tracks && tracks.length
                ? tracks.map((track) => track).join("-")
                : noTrans("not set"),
        variant,
    };

    trackTillsammansEvent({
        event: PLACE_BET,
        bettingSystem: "tillsammans",
        userDate: today.format("YYYY-MM-DD"),
        ecommerce: {
            purchase: {
                actionField: {
                    action: "purchase",
                    id,
                    revenue: `${fromMinor(stake)}`,
                },
                products: [product],
            },
        },
    });
};

export const trackSharedBetGamePanelClick = ({
    event = PRODUCT_PAGE_V85,
    area = noTrans("Tillsammans"),
    clickText,
}: {
    event?: Uncapitalize<string>;
    area?: string;
    clickText: string;
}) => {
    trackEvent({
        event,
        area,
        clickText,
    });
};

export const trackCaptainExclusiveTipEvent = ({
    gameType,
    slug,
}: {
    gameType: string;
    slug?: string;
}) => {
    trackTillsammansEvent({
        event: CAPTAIN_EXCLUSIVE_TIP,
        gameType,
        slug,
    });
};

export const trackMyTeamsPageClick = ({
    pageObject,
    area,
}: {
    pageObject: string;
    area?: string;
}) => {
    trackTillsammansEvent({
        event: MY_TEAMS_PAGE_CLICK,
        teamCardObject: pageObject,
        area,
    });
};

export const trackToggleStakeChoiceEvent = ({choice}: {choice?: string}) => {
    trackTillsammansEvent({
        event: TOGGLE_STAKE_CHOICE,
        choice,
    });
};

export const trackMyCouponEvent = ({choice}: {choice?: string}) => {
    trackTillsammansEvent({
        event: MY_COUPON_CLICK,
        choice,
    });
};
